import {
  getPrincipleInterest,
  findInterestOnlyPayment,
  calculateAPRForHELOAN,
  getTotalInterest,
} from '@elevationscu/common-mortgage-calculator';
import { LoanCompareBase } from '@elevationscu/ui-component-lib';
import {
  calculateEstimatedMonthlyPayment,
  fixNaN,
  getMathSymbol,
  getTermInMonths,
  getHighlightedOption,
  isHeloc,
  isHeloan,
  isHomeEquity,
  isHeloan3020,
  isARM,
} from '../utils/Operations';

export class LoanCompare extends LoanCompareBase {
  constructor(data = {}, loan = {}, calculatedLoanAmount = 0, cashRequested = 0, name = '', ltv = 0, reason = '', closingCosts = 0) {
    super();
    this.selectedLoan = name;
    this.highlight = getHighlightedOption(reason);
    this.interestRate = parseFloat(data?.rate || 0);
    this.apr = (data?.apr || 0);
    this.termInMonths = loan?.estimatedMortageBalance !== undefined ? getTermInMonths(name) : 0;
    this.productId = data?.productId || 0; // this product ID is the ID of the OB product
    this.currentLoanAmount = parseFloat(fixNaN(calculatedLoanAmount));
    this.currentMonthlyPayment = parseFloat(fixNaN(loan?.estimatedMonthlyPayment));
    this.newPrincipalAndInterest = (data?.principalAndInterest || 0);
    this.cashAtClosing = 0;
    this.cashDueOrRefundAtClosing = 'due';
    this.loanToValue = ltv;

    const heloanSelected = isHeloan(this.selectedLoan);
    const helocSelected = isHeloc(this.selectedLoan);
    const heloan3020Selected = isHeloan3020(this.selectedLoan);
    const homeEquitySelected = isHomeEquity(this.selectedLoan);
    const armSelected = isARM(this.selectedLoan);
    // Discount and Rebate Logic
    const discount = data?.discount ?? 0;
    const rebate = data?.rebate ?? 0;
    this.hasDiscount = discount > 0 || (discount === 0 && rebate === 0);
    this.discountOrRebate = this.hasDiscount ? discount : rebate;

    this.price = data?.price || 0;
    this.selectedLoan = data?.selectedLoan || name;
    this.productName = data?.productName || '';
    this.error = false;
    // HELOAN and HELOC min check return error if under $10,000
    if (heloanSelected || helocSelected) {
      if (cashRequested < 10000) {
        this.error = true;
      }
    }

    // HELOAN
    if (heloanSelected || heloan3020Selected) {
      this.newPrincipalAndInterest = this.interestRate > 0 && cashRequested > 9999 ? this.calculateNewPrincipalAndInterest(this.interestRate, 240, this.currentLoanAmount) : 0;
      this.monthlyPaymentChange = this.newPrincipalAndInterest;
      this.monthlyInterestChange = fixNaN(this.calculateMonthlyInterestChange(loan, heloanSelected) || 0);
      // HELOC
    } else if (helocSelected) {
      this.currentLoanAmount = parseFloat(cashRequested);
      this.cashRequested = parseFloat(cashRequested);
      this.monthlyPaymentChange = this.getMontlyInterest(this.interestRate) * this.cashRequested;
      this.homeEquitytotalIntSavings = 'Variable';
      this.monthlyInterestChange = this.monthlyPaymentChange;
    }

    if (heloan3020Selected || armSelected) {
      this.homeEquitytotalIntSavings = 'Variable';
    }

    this.totalInterest = 0;
    try {
      if (!helocSelected && this.currentLoanAmount > 0 && this.interestRate > 0 && this.newPrincipalAndInterest > 0) {
        this.totalInterest = getTotalInterest(
          this.currentLoanAmount,
          this.interestRate / 100,
          this.newPrincipalAndInterest,
        );
      }
    } catch (e) {
      console.warn('getTotalInterest failed:', e.message);
    }

    // HELOC / HELOAN
    if (homeEquitySelected) {
      this.totalInterestChange = heloanSelected ? fixNaN(this.calculateTotalInterestChange(loan, heloanSelected)) : 0;
      this.apr = heloanSelected ? calculateAPRForHELOAN(this.currentLoanAmount, this.totalInterest, closingCosts) : this.interestRate;
      // REFI
    } else {
      this.monthlyPaymentChange = fixNaN(this.calculatePaymentChange(loan) || 0);
      this.monthlyInterestChange = fixNaN(this.calculateMonthlyInterestChange(loan) || 0);
      this.totalInterestChange = fixNaN(this.calculateTotalInterestChange(loan) || 0);
    }

    this.newPayment = homeEquitySelected ? this.currentMonthlyPayment + fixNaN(parseFloat(this.monthlyPaymentChange)) : fixNaN(this.calculateNewPayment(loan, ltv)) || 0;
    this.paymentTitle = homeEquitySelected ? `${this.selectedLoan} + CURRENT PAYMENT` : 'NEW PAYMENT';
    this.totalIntPosNeg = getMathSymbol(this.totalInterestChange);

    this.paymentChange = Math.abs(this.monthlyPaymentChange);
    this.paymentPosNeg = getMathSymbol(this.monthlyPaymentChange);
    this.monthlyIntDiff = Math.abs(this.monthlyInterestChange);
    this.monthIntPosNeg = getMathSymbol(this.monthlyInterestChange);
    this.totalIntSavings = Math.abs(this.totalInterestChange);
  }

  calculateTotalInterestChange(loan, heloanSelected = false) {
    const newLoanTotalInterest = (this.newPrincipalAndInterest * this.termInMonths) - this.currentLoanAmount;
    if (heloanSelected) {
      return newLoanTotalInterest;
    }
    return newLoanTotalInterest - fixNaN(loan?.estimatedRemainingInterest);
  }

  getMontlyInterest(rate) {
    return (rate / 100) / 12;
  }

  calculateMonthlyInterestChange(loan, heloanSelected = false) {
    const newInterest = findInterestOnlyPayment(this.getMontlyInterest(this.interestRate), this.currentLoanAmount);
    if (heloanSelected) {
      return newInterest;
    }
    const currentInterest = findInterestOnlyPayment(this.getMontlyInterest(loan?.estimatedInterestRate), loan?.estimatedMortageBalance);
    return newInterest - currentInterest;
  }

  calculatePaymentChange(loan) {
    return this.newPrincipalAndInterest - parseFloat(fixNaN(loan?.estimatedPrincipalAndInterest) ?? 0);
  }

  calculateNewPayment(loan = {}, ltv = 0) {
    const PMI = ltv >= 80 ? parseFloat(loan?.estimatedMortgageInsurance || 0) : 0;
    return fixNaN(calculateEstimatedMonthlyPayment(
      this.newPrincipalAndInterest,
      parseFloat(loan?.estimatedTaxes),
      parseFloat(loan?.estimatedPropertyInsurance),
      parseFloat(loan?.estimatedFloodInsurance),
      PMI,
    ));
  }

  // will be used for HELOC/HELOAN
  calculateNewPrincipalAndInterest() {
    return getPrincipleInterest(
      this.interestRate / 100,
      this.termInMonths,
      this.currentLoanAmount,
    );
  }
}
